import React, { useState, useEffect, useRef } from 'react'
import { useParams } from 'react-router-dom'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../../ui/card'
import { Button } from '../../ui/button'
import { Input } from '../../ui/input'
import { Label } from '../../ui/label'
import { Checkbox } from '../../ui/checkbox'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../ui/dialog'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../ui/table'
import { Badge } from '../../ui/badge'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../ui/select'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../../ui/dropdown-menu'
import {
  Users,
  Plus,
  Edit,
  MoreHorizontal,
  Archive,
  ArchiveRestore,
  Download,
  UserCircle,
  Upload,
  Loader2,
  AlertCircle,
} from 'lucide-react'
import EventEndpoints from '../../../endpoints/EventEndpoints'
import { useEventContext } from '../EventContext'
import {
  GenericIdentityGroup,
  IdentityRole,
  Event as EventModel,
} from '../../../model/Event'
import {
  GenericIdentityGroupRequest,
  GenericIdentityGroupRequestFromGroup,
} from '../../../requests/GenericIdentityGroupRequest'
import { toast } from 'sonner'
import { BadgePreviewDialog } from './BadgePreviewDialog'

interface FormData {
  roleId: number | null
  eventNameAbbreviation: string
  quantity: number
  base64Image: string
  imageContentType: string
}

const defaultFormData: FormData = {
  roleId: null,
  eventNameAbbreviation: '',
  quantity: 1,
  base64Image: '',
  imageContentType: '',
}

const MAX_IMAGE_DIMENSION = 800
const JPEG_QUALITY = 0.85

const formatKB = (bytes: number) => Math.max(1, Math.round(bytes / 1024))

const base64SizeInBytes = (base64: string) =>
  Math.floor((base64.length * 3) / 4)

const downscaleImageFile = async (
  file: File
): Promise<{ base64: string; contentType: string; size: number }> => {
  const dataUrl = await new Promise<string>((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(reader.result as string)
    reader.onerror = () => reject(reader.error)
    reader.readAsDataURL(file)
  })

  const img = await new Promise<HTMLImageElement>((resolve, reject) => {
    const i = new Image()
    i.onload = () => resolve(i)
    i.onerror = () => reject(new Error('Could not decode image'))
    i.src = dataUrl
  })

  const longest = Math.max(img.width, img.height)
  const scale = longest > MAX_IMAGE_DIMENSION ? MAX_IMAGE_DIMENSION / longest : 1
  const w = Math.round(img.width * scale)
  const h = Math.round(img.height * scale)

  const canvas = document.createElement('canvas')
  canvas.width = w
  canvas.height = h
  const ctx = canvas.getContext('2d')
  if (!ctx) throw new Error('Canvas 2D context not available')
  ctx.fillStyle = '#ffffff'
  ctx.fillRect(0, 0, w, h)
  ctx.drawImage(img, 0, 0, w, h)

  const outputDataUrl = canvas.toDataURL('image/jpeg', JPEG_QUALITY)
  const base64 = outputDataUrl.split(',')[1]
  return { base64, contentType: 'image/jpeg', size: base64SizeInBytes(base64) }
}

export const GenericIdentities: React.FC = () => {
  const { eventId } = useParams<{ eventId: string }>()
  const { event, setEvent, canEdit } = useEventContext()
  const [groups, setGroups] = useState<GenericIdentityGroup[]>([])
  const [roles, setRoles] = useState<IdentityRole[]>([])
  const [saving, setSaving] = useState(false)
  const [includeArchived, setIncludeArchived] = useState(false)

  const [createDialogOpen, setCreateDialogOpen] = useState(false)
  const [editDialogOpen, setEditDialogOpen] = useState(false)
  const [selectedGroup, setSelectedGroup] = useState<GenericIdentityGroup | null>(null)

  const [formData, setFormData] = useState<FormData>(defaultFormData)
  const [formErrors, setFormErrors] = useState<Record<string, string>>({})
  const fileInputRef = useRef<HTMLInputElement>(null)

  const [previewDialog, setPreviewDialog] = useState<{
    open: boolean
    blob: Blob | null
    filename: string
  }>({ open: false, blob: null, filename: '' })

  const [imageSize, setImageSize] = useState<{
    original: number
    scaled: number
  } | null>(null)

  useEffect(() => {
    if (event) {
      setGroups(event.genericIdentityGroups || [])
      setRoles(event.roles || [])
    }
  }, [event])

  const pendingGroupIdsKey = groups
    .filter((g) => g.badgeStatus === 'PENDING')
    .map((g) => g.id)
    .sort((a, b) => a - b)
    .join(',')

  useEffect(() => {
    if (!pendingGroupIdsKey) return

    const pendingIds = pendingGroupIdsKey.split(',').map(Number)
    const attempts: Record<number, number> = {}
    const MAX_ATTEMPTS = 100 // ~5 minutes at 3s

    const tick = async () => {
      for (const id of pendingIds) {
        attempts[id] = (attempts[id] || 0) + 1
        if (attempts[id] > MAX_ATTEMPTS) {
          setGroups((prev) =>
            prev.map((g) => (g.id === id ? { ...g, badgeStatus: 'FAILED' } : g))
          )
          continue
        }
        try {
          const result = await EventEndpoints.getGenericIdentityGroupBadgeStatus(id)
          if (result.badgeStatus !== 'PENDING') {
            setGroups((prev) =>
              prev.map((g) => (g.id === id ? { ...g, badgeStatus: result.badgeStatus } : g))
            )
          }
        } catch {
          // swallow; retry next tick
        }
      }
    }

    const interval = window.setInterval(tick, 3000)
    return () => window.clearInterval(interval)
  }, [pendingGroupIdsKey])

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    const originalSize = file.size

    try {
      const { base64, contentType, size } = await downscaleImageFile(file)
      setFormData((prev) => ({
        ...prev,
        base64Image: base64,
        imageContentType: contentType,
      }))
      setImageSize({ original: originalSize, scaled: size })
      if (formErrors.image) {
        setFormErrors((prev) => ({ ...prev, image: '' }))
      }
    } catch (err: any) {
      toast.error(err?.message || 'Could not process image')
    } finally {
      // allow re-uploading the same file later (input emits change only on different value)
      e.target.value = ''
    }
  }

  const validateForm = (data: FormData, isEdit: boolean): Record<string, string> => {
    const errors: Record<string, string> = {}
    if (!data.roleId) errors.roleId = 'A role is required'
    if (!data.eventNameAbbreviation.trim()) errors.eventNameAbbreviation = 'Event name abbreviation is required'
    if (data.quantity < 1) errors.quantity = 'Quantity must be at least 1'
    if (isEdit && selectedGroup && data.quantity < selectedGroup.identityCount) {
      errors.quantity = `Quantity cannot be less than ${selectedGroup.identityCount} (current count)`
    }
    if (!data.base64Image && !isEdit) errors.image = 'An image is required'
    return errors
  }

  const buildRequest = (data: FormData): GenericIdentityGroupRequest => ({
    roleId: data.roleId!,
    eventNameAbbreviation: data.eventNameAbbreviation,
    quantity: data.quantity,
    base64Image: data.base64Image,
    imageContentType: data.imageContentType,
  })

  const handleCreate = async () => {
    const errors = validateForm(formData, false)
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors)
      return
    }

    try {
      setSaving(true)
      const result = await EventEndpoints.addGenericIdentityGroup(parseInt(eventId!), buildRequest(formData))
      setEvent(result.parent)
      setGroups(result.parent.genericIdentityGroups || [])
      toast.success('Generic identity group created')
      setCreateDialogOpen(false)
      setFormData(defaultFormData)
      setFormErrors({})
      setImageSize(null)
    } catch (error: any) {
      const errorMsg = error?.response?.data?.message || error?.message || 'Failed to create group'
      toast.error(errorMsg)
    } finally {
      setSaving(false)
    }
  }

  const handleEdit = async () => {
    if (!selectedGroup) return

    const errors = validateForm(formData, true)
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors)
      return
    }

    setSaving(true)

    try {
      const request = buildRequest(formData)
      // If no new image was uploaded, use the existing one
      if (!request.base64Image && selectedGroup.picture) {
        request.base64Image = selectedGroup.picture.data
        request.imageContentType = selectedGroup.picture.contentType
      }
      const updatedEvent = await EventEndpoints.editGenericIdentityGroup(selectedGroup.id, request)
      setEvent(updatedEvent)
      setGroups(updatedEvent.genericIdentityGroups || [])
      toast.success('Generic identity group updated')
      setEditDialogOpen(false)
      setSelectedGroup(null)
      setFormData(defaultFormData)
      setFormErrors({})
      setImageSize(null)
    } catch (error: any) {
      const errorMsg = error?.response?.data?.message || error?.message || 'Failed to update group'
      toast.error(errorMsg)
    } finally {
      setSaving(false)
    }
  }

  const handleArchiveToggle = async (group: GenericIdentityGroup) => {
    setSaving(true)
    try {
      let updatedEvent: EventModel
      if (group.dateArchived) {
        updatedEvent = await EventEndpoints.unarchiveGenericIdentityGroup(group.id)
        toast.success('Group unarchived')
      } else {
        updatedEvent = await EventEndpoints.archiveGenericIdentityGroup(group.id)
        toast.success('Group archived')
      }
      setEvent(updatedEvent)
      setGroups(updatedEvent.genericIdentityGroups || [])
    } catch (error: any) {
      const errorMsg = error?.response?.data?.message || error?.message || 'Failed to update archive status'
      toast.error(errorMsg)
    } finally {
      setSaving(false)
    }
  }

  const handleDownloadBadges = async (group: GenericIdentityGroup) => {
    try {
      const response = await EventEndpoints.getGenericIdentityGroupBadges(group.id)
      const blob = response instanceof Blob ? response : await response.blob()
      setPreviewDialog({
        open: true,
        blob,
        filename: `badges-${group.eventNameAbbreviation}.pdf`,
      })
    } catch (error: any) {
      const errorMsg = error?.response?.data?.message || error?.message || 'Failed to download badges'
      toast.error(errorMsg)
    }
  }

  const openCreateDialog = () => {
    setSelectedGroup(null)
    const sortedRoles = [...roles].sort((a, b) => a.internalName.localeCompare(b.internalName))
    setFormData({
      ...defaultFormData,
      roleId: sortedRoles.length > 0 ? sortedRoles[0].id : null,
    })
    setFormErrors({})
    setImageSize(null)
    setCreateDialogOpen(true)
  }

  const openEditDialog = (group: GenericIdentityGroup) => {
    setSelectedGroup(group)
    setFormData({
      roleId: group.role.id,
      eventNameAbbreviation: group.eventNameAbbreviation,
      quantity: group.identityCount,
      base64Image: group.picture?.data || '',
      imageContentType: group.picture?.contentType || '',
    })
    setFormErrors({})
    const existing = group.picture?.data
      ? base64SizeInBytes(group.picture.data)
      : 0
    setImageSize(existing > 0 ? { original: existing, scaled: existing } : null)
    setEditDialogOpen(true)
  }

  if (!event) {
    return (
      <div className="text-center py-8">
        <p className="text-muted-foreground">Loading...</p>
      </div>
    )
  }

  const filteredGroups = groups.filter((g) => includeArchived || !g.dateArchived)
  const sortedGroups = [...filteredGroups].sort((a, b) =>
    a.role.internalName.localeCompare(b.role.internalName)
  )

  const isEditing = !!selectedGroup

  const groupFormFields = (
    <div className="grid gap-4 py-4">
      <div className="grid grid-cols-4 items-center gap-4">
        <Label className="text-right">Role *</Label>
        <div className="col-span-3">
          <Select
            value={formData.roleId?.toString() || ''}
            onValueChange={(value) =>
              setFormData((prev) => ({ ...prev, roleId: parseInt(value) }))
            }
            disabled={isEditing}
          >
            <SelectTrigger className={formErrors.roleId ? 'border-red-500' : ''}>
              <SelectValue placeholder="Select a role" />
            </SelectTrigger>
            <SelectContent>
              {[...roles]
                .sort((a, b) => a.internalName.localeCompare(b.internalName))
                .map((role) => (
                  <SelectItem key={role.id} value={role.id.toString()}>
                    {role.internalName}
                  </SelectItem>
                ))}
            </SelectContent>
          </Select>
          {formErrors.roleId && (
            <p className="text-sm text-red-500 mt-1">{formErrors.roleId}</p>
          )}
          {isEditing && (
            <p className="text-xs text-muted-foreground mt-1">Role cannot be changed after creation</p>
          )}
        </div>
      </div>
      <div className="grid grid-cols-4 items-center gap-4">
        <Label htmlFor="abbreviation" className="text-right">Abbreviation *</Label>
        <div className="col-span-3">
          <Input
            id="abbreviation"
            value={formData.eventNameAbbreviation}
            onChange={(e) => setFormData((prev) => ({ ...prev, eventNameAbbreviation: e.target.value }))}
            placeholder="Shown on badges instead of last name"
            className={formErrors.eventNameAbbreviation ? 'border-red-500' : ''}
          />
          {formErrors.eventNameAbbreviation && (
            <p className="text-sm text-red-500 mt-1">{formErrors.eventNameAbbreviation}</p>
          )}
        </div>
      </div>
      <div className="grid grid-cols-4 items-center gap-4">
        <Label htmlFor="quantity" className="text-right">Quantity *</Label>
        <div className="col-span-3">
          <Input
            id="quantity"
            type="number"
            min={isEditing && selectedGroup ? selectedGroup.identityCount : 1}
            value={formData.quantity}
            onChange={(e) => setFormData((prev) => ({ ...prev, quantity: parseInt(e.target.value) || 0 }))}
            className={formErrors.quantity ? 'border-red-500' : ''}
          />
          {formErrors.quantity && (
            <p className="text-sm text-red-500 mt-1">{formErrors.quantity}</p>
          )}
          {isEditing && selectedGroup && (
            <p className="text-xs text-muted-foreground mt-1">
              Cannot be less than {selectedGroup.identityCount} (current count)
            </p>
          )}
        </div>
      </div>
      <div className="grid grid-cols-4 items-start gap-4">
        <Label className="text-right pt-1">Image {!isEditing && '*'}</Label>
        <div className="col-span-3 space-y-2">
          {formData.base64Image && (
            <div className="relative inline-block">
              <img
                src={`data:${formData.imageContentType};base64,${formData.base64Image}`}
                alt="Badge"
                className="h-20 w-20 object-cover rounded border"
              />
              <Button
                variant="destructive"
                size="icon"
                className="absolute -top-2 -right-2 h-5 w-5 rounded-full"
                onClick={() => {
                  setFormData((prev) => ({ ...prev, base64Image: '', imageContentType: '' }))
                  setImageSize(null)
                }}
              >
                x
              </Button>
            </div>
          )}
          {imageSize && (
            <p className="text-xs text-muted-foreground">
              Image size: {formatKB(imageSize.scaled)} KB
              {imageSize.original > imageSize.scaled && (
                <> (scaled from {formatKB(imageSize.original)} KB)</>
              )}
            </p>
          )}
          <div>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleFileChange}
              className="hidden"
            />
            <Button
              variant="outline"
              type="button"
              onClick={() => fileInputRef.current?.click()}
            >
              <Upload className="h-4 w-4 mr-2" />
              {formData.base64Image ? 'Change Image' : 'Upload Image'}
            </Button>
          </div>
          {formErrors.image && (
            <p className="text-sm text-red-500">{formErrors.image}</p>
          )}
        </div>
      </div>
    </div>
  )

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Users className="h-5 w-5" />
                Generic Identity Groups
              </CardTitle>
              <CardDescription>
                Pre-created identity batches automatically provisioned to {event?.name || 'this event'}
              </CardDescription>
            </div>
            <div className="flex items-center gap-4">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="includeArchivedGroups"
                  checked={includeArchived}
                  onCheckedChange={(checked) => setIncludeArchived(checked === true)}
                />
                <Label htmlFor="includeArchivedGroups" className="text-sm font-normal whitespace-nowrap">
                  Include Archived
                </Label>
              </div>
              <Button
                onClick={openCreateDialog}
                disabled={roles.length === 0 || !canEdit}
              >
                <Plus className="h-4 w-4 mr-2" />
                Create Group
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {roles.length === 0 ? (
            <div className="text-center py-8">
              <UserCircle className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <h3 className="text-lg font-semibold mb-2">No roles available</h3>
              <p className="text-muted-foreground">
                Create at least one role in the Roles tab before creating generic identity groups.
              </p>
            </div>
          ) : sortedGroups.length === 0 ? (
            <div className="text-center py-8">
              <UserCircle className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <h3 className="text-lg font-semibold mb-2">No generic identity groups</h3>
              <p className="text-muted-foreground mb-4">
                Create your first group to batch-provision generic identities.
              </p>
              <Button onClick={openCreateDialog} disabled={!canEdit}>
                <Plus className="h-4 w-4 mr-2" />
                Create Group
              </Button>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Role</TableHead>
                  <TableHead>Abbreviation</TableHead>
                  <TableHead className="text-center"># Identities</TableHead>
                  <TableHead className="text-center">Badges</TableHead>
                  <TableHead className="text-center">Status</TableHead>
                  <TableHead className="w-[50px]" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {sortedGroups.map((group) => (
                  <TableRow
                    key={group.id}
                    className={group.dateArchived ? 'bg-yellow-50/50' : ''}
                  >
                    <TableCell className="font-medium">{group.role.internalName}</TableCell>
                    <TableCell>{group.eventNameAbbreviation}</TableCell>
                    <TableCell className="text-center">{group.identityCount}</TableCell>
                    <TableCell className="text-center">
                      {group.badgeStatus === 'PENDING' && (
                        <Badge variant="secondary" className="text-xs bg-blue-50 text-blue-700 hover:bg-blue-50 inline-flex items-center">
                          <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                          Generating
                        </Badge>
                      )}
                      {group.badgeStatus === 'READY' && (
                        <Badge variant="secondary" className="text-xs bg-green-50 text-green-700 hover:bg-green-50">
                          Ready
                        </Badge>
                      )}
                      {group.badgeStatus === 'FAILED' && (
                        <Badge variant="secondary" className="text-xs bg-red-50 text-red-700 hover:bg-red-50 inline-flex items-center">
                          <AlertCircle className="h-3 w-3 mr-1" />
                          Failed
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-center">
                      {group.dateArchived ? (
                        <Badge variant="secondary" className="text-xs bg-yellow-100 text-yellow-800 hover:bg-yellow-100">
                          Archived
                        </Badge>
                      ) : (
                        <Badge variant="secondary" className="text-xs bg-green-50 text-green-700 hover:bg-green-50">
                          Active
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" className="h-8 w-8 p-0">
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          {canEdit && (
                            <DropdownMenuItem onClick={() => openEditDialog(group)}>
                              <Edit className="h-4 w-4 mr-2" />
                              Edit
                            </DropdownMenuItem>
                          )}
                          {group.badgeStatus === 'PENDING' ? (
                            <DropdownMenuItem disabled>
                              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                              Generating Badges...
                            </DropdownMenuItem>
                          ) : group.badgeStatus === 'FAILED' ? (
                            <DropdownMenuItem
                              onClick={() =>
                                toast.error(
                                  'Badge generation failed. Edit the group to regenerate.'
                                )
                              }
                            >
                              <AlertCircle className="h-4 w-4 mr-2 text-red-500" />
                              Generation Failed
                            </DropdownMenuItem>
                          ) : (
                            <DropdownMenuItem onClick={() => handleDownloadBadges(group)}>
                              <Download className="h-4 w-4 mr-2" />
                              Download Badges
                            </DropdownMenuItem>
                          )}
                          {canEdit && (
                            <>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem onClick={() => handleArchiveToggle(group)}>
                                {group.dateArchived ? (
                                  <>
                                    <ArchiveRestore className="h-4 w-4 mr-2" />
                                    Unarchive
                                  </>
                                ) : (
                                  <>
                                    <Archive className="h-4 w-4 mr-2" />
                                    Archive
                                  </>
                                )}
                              </DropdownMenuItem>
                            </>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Create Dialog */}
      <Dialog
        open={createDialogOpen}
        onOpenChange={(open) => {
          if (!open && saving) return
          setCreateDialogOpen(open)
          if (!open) {
            setFormData(defaultFormData)
            setFormErrors({})
            setImageSize(null)
          }
        }}
      >
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle>Create Generic Identity Group</DialogTitle>
            <DialogDescription>Create a batch of generic identities for this event.</DialogDescription>
          </DialogHeader>
          {groupFormFields}
          <DialogFooter>
            <Button
              variant="outline"
              disabled={saving}
              onClick={() => {
                setCreateDialogOpen(false)
                setFormData(defaultFormData)
                setFormErrors({})
                setImageSize(null)
              }}
            >
              Cancel
            </Button>
            <Button onClick={handleCreate} disabled={saving}>
              {saving ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Creating...
                </>
              ) : (
                'Create Group'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog
        open={editDialogOpen}
        onOpenChange={(open) => {
          if (!open && !saving) {
            setEditDialogOpen(false)
            setFormData(defaultFormData)
            setFormErrors({})
            setSelectedGroup(null)
            setImageSize(null)
          }
        }}
      >
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle>Edit Generic Identity Group</DialogTitle>
            <DialogDescription>Update the group details.</DialogDescription>
          </DialogHeader>
          {groupFormFields}
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setEditDialogOpen(false)
                setFormData(defaultFormData)
                setFormErrors({})
                setSelectedGroup(null)
                setImageSize(null)
              }}
              disabled={saving}
            >
              Cancel
            </Button>
            <Button onClick={handleEdit} disabled={saving}>
              {saving ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                'Save Changes'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <BadgePreviewDialog
        open={previewDialog.open}
        onOpenChange={(open) => setPreviewDialog((prev) => ({ ...prev, open }))}
        pdfBlob={previewDialog.blob}
        filename={previewDialog.filename}
      />
    </div>
  )
}
